import { Alert, Box, CircularProgress, Paper, Typography, useTheme } from "@mui/material";
import { LineChart } from '@mui/x-charts/LineChart';
import { useMemo } from "react";
import { useTrendsForecast } from "../hooks/useTrendsForecast";

const CHART_HEIGHT = 400;
const MILLIONS_DIVISOR = 1_000_000;

export const ProgrammeFinancialTrends = () => {
    const theme = useTheme();
    const { data, isLoading, isError, error } = useTrendsForecast();

    const chartData = useMemo(() => {
        if (!data || !data.programmes || data.programmes.length === 0) {
            return {
                timeLabels: [],
                actualsData: [],
                budgetData: [],
                commitmentsData: [],
                eacData: [],
            };
        }

        const aggregatedData = new Map<string, {
            actuals: number;
            budget: number;
            commitments: number;
            eac: number;
        }>();

        data.programmes.forEach(programme => {
            programme.monthly_data.forEach(monthData => {
                const existing = aggregatedData.get(monthData.month) || {
                    actuals: 0,
                    budget: 0,
                    commitments: 0,
                    eac: 0,
                };
                
                aggregatedData.set(monthData.month, {
                    actuals: existing.actuals + monthData.actuals,
                    budget: existing.budget + monthData.budget,
                    commitments: existing.commitments + monthData.commitments,
                    eac: existing.eac + monthData.eac,
                });
            });
        });

        const sortedMonths = Array.from(aggregatedData.keys()).sort();

        return {
            timeLabels: sortedMonths,
            actualsData: sortedMonths.map(month => aggregatedData.get(month)!.actuals / MILLIONS_DIVISOR),
            budgetData: sortedMonths.map(month => aggregatedData.get(month)!.budget / MILLIONS_DIVISOR),
            commitmentsData: sortedMonths.map(month => aggregatedData.get(month)!.commitments / MILLIONS_DIVISOR),
            eacData: sortedMonths.map(month => aggregatedData.get(month)!.eac / MILLIONS_DIVISOR),
        };
    }, [data]);

    if (isLoading) {
        return (
            <Paper variant="outlined" sx={{ m: 2, mb: 3, p: 2 }}>
                <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
                    Programme Financial Trends
                </Typography>
                <Box
                    height={CHART_HEIGHT}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    if (isError) {
        return (
            <Paper variant="outlined" sx={{ m: 2, mb: 3, p: 2 }}>
                <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
                    Programme Financial Trends
                </Typography>
                <Box
                    height={CHART_HEIGHT}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Alert severity="warning">
                        {error?.message || "Failed to load trends data"}
                    </Alert>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ m: 2, mb: 3, p: 2 }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
                Programme Financial Trends
            </Typography>
            <LineChart
                xAxis={[{
                    scaleType: 'point',
                    data: chartData.timeLabels,
                }]}
                yAxis={[{
                    label: '£M',
                    min: 0,
                }]}
                series={[
                    {
                        data: chartData.actualsData,
                        label: 'Actuals',
                        color: theme.palette.success.main,
                        showMark: true,
                    },
                    {
                        data: chartData.budgetData,
                        label: 'Budget',
                        color: theme.palette.grey[500],
                        showMark: true,
                    },
                    {
                        data: chartData.commitmentsData,
                        label: 'Commitments',
                        color: theme.palette.info.main,
                        showMark: true,
                    },
                    {
                        data: chartData.eacData,
                        label: 'EAC',
                        color: theme.palette.warning.main,
                        showMark: true,
                    },
                ]}
                height={CHART_HEIGHT}
                grid={{ vertical: true, horizontal: true }}
            />
        </Paper>
    );
};
